import { getS3SessionSnapshot } from '@/services/s3Session'


// ADR-0016: tiles are served same-origin through CloudFront at /s3/lyr/...
// (the CloudFront function strips the /s3/lyr prefix to the S3 key). Returns
// null until the org's signed cookies are set, so callers fall back to the
// Tulum proxy / skip during the gap — same contract as the old STS session.
function s3Origin() {
  if (!getS3SessionSnapshot()) return null
  return '/s3/lyr'
}

function hasStoragePointers(row) {
  return Boolean(row?.storage_bucket && row?.storage_prefix)
}

export function buildLulcTileUrl(orgId, layerUuid, row) {
  if (!hasStoragePointers(row)) {
    return `/tulum/tiles/lulc/${layerUuid}/{z}/{x}/{y}.png`
  }
  const origin = s3Origin()
  if (!origin) return null
  return `${origin}/${orgId}/layers/${layerUuid}/tiles/{z}/{x}/{y}.png`
}

export function buildSubscriptionTileUrl(orgId, layerCode, resultId, row) {
  if (!hasStoragePointers(row)) {
    // Pre-cutover tiles were keyed by planet_subscription_id, not layer_subscription.code.
    const planetSubId = row?.planet_subscription_id
    if (!planetSubId) return null
    return `/tulum/tiles/subscriptions/${planetSubId}/${resultId}/{z}/{x}/{y}.png`
  }
  const origin = s3Origin()
  if (!origin) return null
  return `${origin}/${orgId}/layers/${layerCode}/${resultId}/tiles/{z}/{x}/{y}.png`
}

// Order tiles (incl. SkySat suborders from tasking). New layout copies from
// s3://earthpbc-orders/orders/{planet_order_id}/tiles/{bundle}/... into
// s3://earthpbc-layers-prod/{org_id}/layers/{layer_order.code}/tiles/{bundle}/...
// so a productBundle is required when storage pointers are set — direct-S3
// has no per-request fallback to a bundleless `tiles/` tree the way the
// Tulum proxy does.
// Generic raster basemap (type=8). `basemapRow` is the layer_basemap sub-object.
// Tile URL priority: tile_url_template (external) → S3 from storage_prefix.
export function buildBasemapTileUrl(basemapRow) {
  if (basemapRow?.tile_url_template) return basemapRow.tile_url_template
  if (!hasStoragePointers(basemapRow)) return null
  const origin = s3Origin()
  if (!origin) return null
  const prefix = basemapRow.storage_prefix.replace(/\/$/, '')
  return `${origin}/${prefix}/{z}/{x}/{y}.png`
}

// Monitoring frame tiles live at {storage_prefix}{result_id}/tiles/... in the
// layers bucket — the payload (frames endpoint response) carries the storage
// pointer, served same-origin through CloudFront like the other builders.
export function buildMonitoringFrameTileUrl(payload, resultId) {
  if (!payload?.storage_prefix) return null
  const origin = s3Origin()
  if (!origin) return null
  return `${origin}/${payload.storage_prefix}${resultId}/tiles/{z}/{x}/{y}.png`
}

export function buildOrderTileUrl(orgId, layerCode, row, productBundle) {
  if (!hasStoragePointers(row)) {
    const planetOrderId = row?.planet_order_id
    if (!planetOrderId) return null
    const qs = productBundle ? `?product_bundle=${encodeURIComponent(productBundle)}` : ''
    return `/tulum/tiles/orders/${planetOrderId}/{z}/{x}/{y}.png${qs}`
  }
  const origin = s3Origin()
  if (!origin) return null
  const bundleSeg = productBundle ? `${encodeURIComponent(productBundle)}/` : ''
  return `${origin}/${orgId}/layers/${layerCode}/tiles/${bundleSeg}{z}/{x}/{y}.png`
}
